import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import "./index.css";

// Components
import NicknamePrompt from "../NicknamePrompt";

// Firebase
import {
  getDoc,
  doc,
  updateDoc,
  arrayUnion,
  onSnapshot,
  arrayRemove,
  setDoc,
} from "firebase/firestore";
import db from "../../firebase/firebase";

const ChatRoom = () => {
  const { room } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const typingTimeoutRef = useRef(null);

  const [roomName, setRoomName] = useState("");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isValidRoom, setIsValidRoom] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [userId, setUserId] = useState(null);
  const [showNicknamePrompt, setShowNicknamePrompt] = useState(true);
  const [usersTyping, setUsersTyping] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const heartbeatIntervalRef = useRef(null);

  // Generate a unique user ID
  const generateUserId = () => {
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Update heartbeat to show user is active
  const updateHeartbeat = useCallback(async (currentUserId) => {
    if (!currentUserId) return;

    try {
      const roomRef = doc(db, "rooms", room);
      const userRef = doc(db, "rooms", room, "users", currentUserId);

      const now = new Date();

      // Update user's last seen timestamp
      await updateDoc(userRef, {
        lastSeen: now,
      });

      // Update active users list in room
      await updateDoc(roomRef, {
        [`activeUsers.${currentUserId}`]: now,
      });
    } catch (error) {
      console.error("Error updating heartbeat:", error);
    }
  }, [room]);

  const handleNicknameSubmit = useCallback(async (nickname) => {
    try {
      const newUserId = generateUserId();
      const roomRef = doc(db, "rooms", room);
      const roomSnap = await getDoc(roomRef);

      if (!roomSnap.exists()) {
        alert("Room does not exist!");
        navigate("/");
        return;
      }

      // Create user entry in the room's users subcollection
      const userRef = doc(db, "rooms", room, "users", newUserId);
      await setDoc(userRef, {
        displayName: nickname,
        joinedAt: new Date(),
        lastSeen: new Date(),
      });

      // Add user ID to the room's users array
      await updateDoc(roomRef, {
        users: arrayUnion(newUserId),
        [`activeUsers.${newUserId}`]: new Date(),
      });

      // Send join message
      await updateDoc(roomRef, {
        messages: arrayUnion({
          userId: newUserId,
          role: "admin",
          displayName: nickname,
          message: `${nickname} joined the room`,
          timeStamp: new Date(),
        }),
      });

      // Save to localStorage for persistence across refreshes
      localStorage.setItem(
        `chatroom_${room}`,
        JSON.stringify({ userId: newUserId, displayName: nickname })
      );

      setUserId(newUserId);
      setDisplayName(nickname);
      setShowNicknamePrompt(false);
      setIsValidRoom(true);
      setIsLoading(false);
    } catch (error) {
      console.error("Error joining room:", error);
      alert("Failed to join room");
      setIsLoading(false);
    }
  }, [room, navigate]);

  const handleTyping = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      const messageToSend = e.target.value.trim();
      if (messageToSend !== "") {
        sendMessage(messageToSend, "user");
        setMessage("");
      }
    } else {
      setMessage(e.target.value);
      updateTypingStatus(true);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(false);
      }, 1250);
    }
  };

  const updateTypingStatus = async (isTyping) => {
    if (!userId) return;

    const docRef = doc(db, "rooms", room);
    const typingUser = { userId, displayName };

    if (isTyping) {
      await updateDoc(docRef, {
        isTyping: arrayUnion(typingUser),
      });
    } else {
      await updateDoc(docRef, {
        isTyping: arrayRemove(typingUser),
      });
    }
  };

  const sendMessage = async (messageText, role) => {
    const docRef = doc(db, "rooms", room);
    const currentDate = new Date();

    await updateDoc(docRef, {
      messages: arrayUnion({
        userId: userId,
        role: role,
        displayName: displayName,
        message: messageText,
        timeStamp: currentDate,
      }),
      isTyping: arrayRemove({ userId, displayName }),
    });
  };

  const convertDate = (timestamp) => {
    if (!timestamp || !timestamp.seconds) {
      return "";
    }
    const milliseconds =
      timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000);
    const date = new Date(milliseconds);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const time = `${hours}:${minutes}:${seconds}`;
    return time;
  };

  const leaveRoom = async () => {
    if (!userId) {
      navigate("/");
      return;
    }

    const docRef = doc(db, "rooms", room);

    // Clear heartbeat interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Send leave message and remove from active users
    await updateDoc(docRef, {
      messages: arrayUnion({
        userId: userId,
        role: "admin",
        displayName: displayName,
        message: `${displayName} left the room`,
        timeStamp: new Date(),
      }),
      users: arrayRemove(userId),
      isTyping: arrayRemove({ userId, displayName }),
      [`activeUsers.${userId}`]: null, // Remove from active users
    });

    // Clear localStorage session
    localStorage.removeItem(`chatroom_${room}`);

    navigate("/");
  };

  // Listen to room changes
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(doc(db, "rooms", room), (snapshot) => {
      if (snapshot.exists()) {
        const roomData = snapshot.data();
        setMessages(roomData.messages || []);
        setRoomName(roomData.roomName);

        // Update typing users (filter out current user)
        const typingData = roomData.isTyping || [];
        const otherUsersTyping = typingData.filter(
          (typingUser) => typingUser.userId !== userId
        );
        setUsersTyping(otherUsersTyping);
      }
    });

    return () => unsubscribe();
  }, [room, userId]);

  // Heartbeat to keep user active
  useEffect(() => {
    if (!userId) return;

    // Send initial heartbeat
    updateHeartbeat(userId);

    // Send heartbeat every 5 seconds
    heartbeatIntervalRef.current = setInterval(() => {
      updateHeartbeat(userId);
    }, 5000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [userId, updateHeartbeat]);

  // Check if room exists and handle creator nickname on initial load
  useEffect(() => {
    const checkRoom = async () => {
      const docRef = doc(db, "rooms", room);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        alert("Room does not exist!");
        navigate("/");
        return;
      }

      // Check localStorage for existing session
      const savedSession = localStorage.getItem(`chatroom_${room}`);
      if (savedSession) {
        const { userId: savedUserId, displayName: savedDisplayName } =
          JSON.parse(savedSession);
        setUserId(savedUserId);
        setDisplayName(savedDisplayName);
        setShowNicknamePrompt(false);
        setIsValidRoom(true);
        setIsLoading(false);
      }
      // If nickname was passed from CreateRoom (creator)
      else if (location.state?.nickname) {
        await handleNicknameSubmit(location.state.nickname);
      } else {
        setIsLoading(false);
      }
    };

    checkRoom();
  }, [room, navigate, location.state?.nickname, handleNicknameSubmit]);

  if (isLoading) {
    return <div className="container loading">Loading...</div>;
  }

  if (showNicknamePrompt) {
    return <NicknamePrompt onSubmit={handleNicknameSubmit} />;
  }

  if (isValidRoom) {
    return (
      <div className="container">
        <h2 className="header">Just Chat @ {roomName}</h2>
        <input
          className="chat-input"
          onKeyDown={handleTyping}
          type="text"
          value={message}
          onChange={(e) => handleTyping(e)}
          placeholder="Message"
        />
        <div className="chat-messages">
          {usersTyping.length > 0 && (
            <p
              className="chat-message-secondary"
              style={{ fontStyle: "italic" }}
            >
              {usersTyping.length === 1
                ? `${usersTyping[0].displayName} is typing...`
                : usersTyping.length === 2
                ? `${usersTyping[0].displayName} and ${usersTyping[1].displayName} are typing...`
                : `${usersTyping.length} people are typing...`}
            </p>
          )}
          {messages
            .slice()
            .reverse()
            .map((msg, index) => (
              <p
                className={
                  msg.role === "admin"
                    ? "chat-message-admin"
                    : msg.userId === userId
                    ? "chat-message-primary"
                    : "chat-message-secondary"
                }
                key={index}
              >
                {msg.role === "admin" ? (
                  <span>{msg.message}</span>
                ) : (
                  <span>
                    <strong>{msg.displayName}</strong> [{convertDate(msg.timeStamp)}
                    ]: {msg.message}
                  </span>
                )}
              </p>
            ))}
        </div>
        <button className="leave-button" onClick={leaveRoom}>
          Leave Room
        </button>
      </div>
    );
  } else {
    return <div className="container loading">Just Chat ;)</div>;
  }
};

export default ChatRoom;
